import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Paper, Typography, Box, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { analyticsApi } from '../../services/api';

interface CategoryTrendData {
  month: string;
  amount: number;
}

interface CategoryTrendsData {
  category_name: string;
  color: string;
  data: CategoryTrendData[];
}

interface CategoryTrendsResponse {
  categories: CategoryTrendsData[];
}

const CategoryTrendsChart: React.FC = () => {
  const [data, setData] = useState<CategoryTrendsData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<number>(6);
  const [selectedCategories, setSelectedCategories] = useState<number>(5);

  const monthOptions = [
    { value: 3, label: '3 Months' },
    { value: 6, label: '6 Months' },
    { value: 12, label: '12 Months' },
    { value: 18, label: '18 Months' }
  ];

  const categoryOptions = [
    { value: 3, label: 'Top 3 Categories' },
    { value: 5, label: 'Top 5 Categories' },
    { value: 8, label: 'Top 8 Categories' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response: CategoryTrendsResponse = await analyticsApi.getCategoryTrends(selectedMonths, selectedCategories);
        setData(response.categories);
        setError(null);
      } catch (err) {
        setError('Failed to load category trends data');
        console.error('Error fetching category trends data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMonths, selectedCategories]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Transform data for recharts
  const transformedData = React.useMemo(() => {
    if (data.length === 0) return [];

    // Get all unique months
    const allMonths = Array.from(
      new Set(data.flatMap(category => category.data.map(item => item.month)))
    ).sort((a, b) => {
      // Simple month sorting (assumes format "Mon YYYY")
      const [monthA, yearA] = a.split(' ');
      const [monthB, yearB] = b.split(' ');
      if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
      
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
    });

    // Create chart data with all months and categories
    return allMonths.map(month => {
      const dataPoint: any = { month };
      data.forEach(category => {
        const monthData = category.data.find(item => item.month === month);
        dataPoint[category.category_name] = monthData ? monthData.amount : 0;
      });
      return dataPoint;
    });
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            p: 2,
            maxWidth: 250
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {label}
          </Typography>
          {payload
            .filter((entry: any) => entry.value > 0)
            .sort((a: any, b: any) => b.value - a.value)
            .map((entry: any, index: number) => (
              <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                {entry.dataKey}: {formatCurrency(entry.value)}
              </Typography>
            ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (data.length === 0 || transformedData.length === 0) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Category Trends Over Time
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
          <Typography variant="body1" color="text.secondary">
            No category trends data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Category Trends Over Time
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 100 } }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={selectedMonths}
              label="Period"
              onChange={(e) => setSelectedMonths(Number(e.target.value))}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
            <InputLabel>Categories</InputLabel>
            <Select
              value={selectedCategories}
              label="Categories"
              onChange={(e) => setSelectedCategories(Number(e.target.value))}
            >
              {categoryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      <Box sx={{ height: { xs: 200, sm: 250, md: 300 }, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={transformedData} margin={{ top: 10, right: 15, left: 10, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month"
              stroke="#666"
              fontSize={10}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              stroke="#666"
              fontSize={11}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {data.map((category, index) => (
              <Line
                key={category.category_name}
                type="monotone"
                dataKey={category.category_name}
                stroke={category.color}
                strokeWidth={2}
                dot={{ fill: category.color, strokeWidth: 2, r: 2 }}
                activeDot={{ r: 4, stroke: category.color, strokeWidth: 2 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Category legend with total spending */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Total Spending by Category:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {data.map((category) => {
            const totalSpending = category.data.reduce((sum, item) => sum + item.amount, 0);
            return (
              <Box
                key={category.category_name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'grey.50'
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: category.color,
                    borderRadius: '50%'
                  }}
                />
                <Typography variant="caption">
                  {category.category_name}: {formatCurrency(totalSpending)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};

export default CategoryTrendsChart;